import api from './api';
import type { PracticeArea } from '../types/practiceArea';

export interface MatterCreate {
  case_number: string;
  cnr_number?: string;
  case_title: string;
  court_name: string;
  /** Stable court slug (courts.ts id). Undefined = court not identified;
   *  the backend then applies NO holiday calendar rather than guessing one. */
  court_id?: string;
  court_type: string;
  case_type: string;
  client_role: string;
  sections_involved?: string[];
  judge_name?: string;
  filing_date?: string;
  date_of_service_of_summons?: string | null;
  next_hearing_date?: string;
  /** Optional "HH:MM", only with a next_hearing_date. */
  next_hearing_time?: string;
  opposing_counsel?: string;
  notes?: string;
  practice_area?: PracticeArea | null;
  forum?: string | null;
  /** Which side of the matter is the platform's own client — 'accused' /
   *  'complainant' / etc., as the court register names it. */
  client_party?: string;
}

/**
 * Allowed values for `Matter.closure_status`. Mirrors the
 * `ClosureStatusLiteral` Pydantic union on the backend
 * (app/schemas/client.py). Keep in sync.
 */
export type ClosureStatus =
  | 'acquitted'
  | 'convicted'
  | 'mixed'
  | 'withdrawn'
  | 'settled'
  | 'dismissed'
  | 'quashed'
  | 'transferred'
  | 'abandoned'
  | 'deceased';

export interface MatterUpdate {
  case_number?: string;
  // `null` explicitly clears the stored value (see backend update_matter which
  // treats an explicit null as "reset this field").
  cnr_number?: string | null;
  case_title?: string;
  court_name?: string;
  /** Stable court slug (courts.ts id). Undefined = court not identified;
   *  the backend then applies NO holiday calendar rather than guessing one. */
  court_id?: string;
  court_type?: string;
  case_type?: string;
  client_role?: string;
  judge_name?: string | null;
  sections_involved?: string[];
  status?: string;
  stage?: string | null;
  filing_date?: string | null;
  date_of_service_of_summons?: string | null;
  next_hearing_date?: string | null;
  /** "HH:MM". The backend drops the stored time whenever next_hearing_date
   *  changes in a request that does not send one. */
  next_hearing_time?: string | null;
  last_hearing_date?: string | null;
  opposing_counsel?: string | null;
  forum?: string | null;
  notes?: string | null;
  // Closure (P0-1) — pass `null` for any of these to clear an existing closure
  closure_status?: ClosureStatus | null;
  closed_at?: string | null;
  closure_notes?: string | null;
  // P2-9: appeal / revision linkage. Pass null to clear; pass a parent
  // matter id the user owns to make this matter an appeal child.
  parent_matter_id?: number | null;
  // Custody / liberty facts (Situation engine). Tri-state booleans use
  // explicit-null semantics: send `null` for "Don't know", omit for no change.
  custody_status?: string;
  custody_start_date?: string;
  arrest_datetime?: string;
  first_production_at?: string;
  chargesheet_filed?: boolean | null;
  chargesheet_filed_date?: string | null;
  // Wave 2 (W2-7): first-time offender, same explicit-null semantics.
  first_time_offender?: boolean | null;
  // Wave 3 (Sibal limb-chip ruling): the ratified charged sub-clause, e.g.
  // "309(4)". Explicit-null semantics: send `null` to clear (re-open the chip),
  // omit for no change — same as first_time_offender / chargesheet_filed.
  charged_limb?: string | null;
  /** Which side of the matter is the platform's own client — 'accused' /
   *  'complainant' / etc. Distinct from `client_role`: this is read back by
   *  the record-findings cross-checks (e.g. `client_party_unknown`). */
  client_party?: string | null;
  /** Free-text ground for a custody-without-process fact the lawyer confirms
   *  despite the `custody_without_process` record finding — persisted so the
   *  finding downgrades to `custody_confirmed_with_reason` on the next read. */
  custody_reason?: string;
}

export interface MatterResponse {
  id: number;
  client_id: string;
  /** Only populated by the cross-client list (listAllMatters); null otherwise. */
  client_name?: string | null;
  case_number: string;
  cnr_number: string | null;
  case_title: string;
  court_name: string;
  /** Stable court slug (courts.ts id). Undefined = court not identified;
   *  the backend then applies NO holiday calendar rather than guessing one. */
  court_id?: string | null;
  court_type: string;
  judge_name: string | null;
  case_type: string;
  client_role: string;
  sections_involved: string[] | null;
  status: string;
  stage: string | null;
  /** Machine-ratifiable stage of the criminal case (backend CRIM_STAGES:
   *  intake / pre_arrest / remand / bail / chargesheet_framing / trial /
   *  judgment_appeal). Distinct from the freeform `stage` above, which holds
   *  diary text. The API has always returned it; it was simply never declared
   *  here, so no consumer could read it. */
  crim_stage?: string | null;
  filing_date: string | null;
  date_of_service_of_summons: string | null;
  next_hearing_date: string | null;
  /** "HH:MM:SS" as the lawyer recorded it; null = not recorded. Optional
   *  (`?`) since older cached responses may predate the column. */
  next_hearing_time?: string | null;
  last_hearing_date: string | null;
  disposal_date: string | null;
  opposing_counsel: string | null;
  notes: string | null;
  practice_area: PracticeArea | null;
  // Civil/consumer forum (null for criminal matters). Optional (`?`) since older
  // cached responses may predate the column.
  forum?: string | null;
  // Closure (P0-1) — null for matters that are still active
  closure_status: ClosureStatus | null;
  closed_at: string | null;
  closure_notes: string | null;
  // P2-9: appeal / revision linkage — null for standalone matters
  parent_matter_id: number | null;
  // Custody / liberty facts (Situation engine, Wave 1-2). Optional (`?`)
  // because older cached responses may predate these columns. Tri-state
  // booleans: null = not answered / "Don't know" — never treat as "No".
  custody_status?: string | null;
  arrest_datetime?: string | null;
  first_production_at?: string | null;
  custody_start_date?: string | null;
  chargesheet_filed?: boolean | null;
  chargesheet_filed_date?: string | null;
  /** Wave 2 (W2-7): tri-state — null = unanswered/"Don't know". */
  first_time_offender?: boolean | null;
  /** Wave 3 (Sibal limb-chip ruling): the ratified charged sub-clause, e.g.
   *  "309(4)". null = not yet ratified (bare parent → chip pending). */
  charged_limb?: string | null;
  /** Which side of the matter is the platform's own client — 'accused' /
   *  'complainant' / etc. null = not yet answered; read back by the
   *  record-findings cross-checks (`client_party_unknown`). */
  client_party?: string | null;
  /** Read-only provenance stamps for situation facts (mirror of
   *  matters.situation_meta): field name → stamp. The backend stamps
   *  origin 'ratified' on every ratify PATCH — including an explicit-null
   *  answer — so a null first_time_offender WITH a ratified stamp is an
   *  answered "Don't know", while null WITHOUT one is never-asked. */
  situation_meta?: Record<
    string,
    { origin?: string; ratified_by?: string; ratified_at?: string; source?: string }
  > | null;
  deadline_count: number;
  document_count: number;
  created_at: string;
  updated_at: string;
  /** Most recent artifact recorded against this matter (case theory, diary
   *  entry, vault doc, generated draft, or matter artifact) — lets the
   *  Matters grid show "what was I last doing" without an LLM call. null
   *  when the matter has no activity yet. */
  last_activity?: MatterTimelineEvent | null;
}

export interface MatterListResponse {
  matters: MatterResponse[];
  total_count: number;
}

// === Top-level matter views (no client_id required) ===

export interface MatterTimelineEvent {
  kind: 'case_theory' | 'hearing_update' | 'deadline' | 'diary_entry' | 'vault_doc' | 'generated_doc' | 'matter_artifact';
  entity_id: string;
  title: string;
  summary: string | null;
  occurred_at: string;
  extra: Record<string, any> | null;
}

export interface MatterTimelineCounts {
  case_theories: number;
  hearings: number;
  deadlines: number;
  diary_entries: number;
  vault_documents: number;
  generated_documents: number;
  matter_artifacts: number;
}

export interface MatterTimelineResponse {
  matter_id: number;
  counts: MatterTimelineCounts;
  events: MatterTimelineEvent[];
  next_cursor: string | null;
}

export interface MatterAttachRequest {
  artifact_type: 'case_theory' | string;
  artifact_id: string;
  auto_export_pdf?: boolean;
}

export interface MatterAttachResponse {
  ok: boolean;
  artifact_type: string | null;
  artifact_id: string | null;
  matter_id: number | null;
  already_attached: boolean;
  vault_doc_id: string | null;
  reason: string | null;
}

/**
 * One-click write of a deterministic limitation date onto a matter.
 * Mirrors `LimitationDeadlineCreate` (backend app/schemas/client.py). Every
 * provenance field is carried over so the resulting deadline is auditable.
 */
export interface LimitationDeadlineCreate {
  deadline_date: string; // ISO date — the forum-check expiry_date
  title?: string;
  dispute_category?: string;
  provision_ref?: string;
  cause_of_action_date?: string;
  status?: string;
  applied_extensions?: string[];
  priority?: string;
  reminder_days?: number;
}

export interface MatterDeadlineResponse {
  id: number;
  matter_id: number;
  title: string;
  description: string | null;
  deadline_date: string;
  deadline_type: string;
  priority: string;
  is_completed: boolean;
  reminder_days: number;
  created_at: string;
  days_remaining: number | null;
  already_exists: boolean;
}

// B4b cross-matter cause-list
export interface CauseListEntry {
  kind: 'hearing' | 'deadline';
  date: string;
  days_remaining: number;
  matter_id: number;
  client_id: string;
  matter_title: string;
  case_number: string | null;
  court_name: string | null;
  forum: string | null;
  deadline_id: number | null;
  deadline_type: string | null;
  priority: string | null;
  title: string | null;
  is_completed: boolean;
  /** Hearing-only: why the matter is listed — the lawyer's diary purpose
   *  ('diary') or the matter's stage as weak context ('stage'), verbatim;
   *  null = not recorded. */
  purpose?: string | null;
  purpose_source?: 'diary' | 'stage' | null;
  /** Hearing-only: "HH:MM:SS" as the lawyer recorded it. null for deadlines
   *  and for hearings with no recorded time — never shown unless recorded. */
  time?: string | null;
}

export interface CauseListResponse {
  entries: CauseListEntry[];
  total: number;
  overdue: number;
  next_7_days: number;
}

// ── Module-level TTL cache for listAllMatters (P3 fan-out mitigation) ──
const ALL_MATTERS_TTL_MS = 60_000;
let _allMattersCache: { ts: number; value: MatterResponse[] } | null = null;
let _allMattersInFlight: Promise<MatterResponse[]> | null = null;

/** Invalidate the listAllMatters cache. Call after creating/closing a matter. */
export function invalidateAllMattersCache(): void {
  _allMattersCache = null;
}

/** Free-trial matter usage for the current user (GET /matters/usage). Powers the
 *  usage meter + disabled "New Matter" button + upgrade CTA. `limit: null` =
 *  unlimited (paid tier). */
export interface MatterUsage {
  tier: string;
  used: number;
  limit: number | null;
  can_create: boolean;
  // Vertical-aware entitlements (optional — backward-compatible with older
  // cached responses / backends that predate the entitlements engine).
  vertical?: string | null;
  entitlement_status?: string;
}

export const mattersApi = {
  createMatter: async (clientId: string, data: MatterCreate): Promise<MatterResponse> => {
    const response = await api.post(`/clients/${clientId}/matters/`, data);
    // A create changes the cross-client matter set — drop the TTL cache so the
    // next listAllMatters (and the usage meter) reflect the new matter.
    invalidateAllMattersCache();
    return response.data;
  },

  /**
   * Re-file a matter under another client of the same workspace (e.g. an imported
   * case filed under the wrong party). Refused by the server when the matter's
   * client has WhatsApp messages about it. The matter keeps its id; only its URL
   * (`/workspace/:clientId/matters/:id`) changes.
   */
  moveMatterToClient: async (matterId: number, clientId: string): Promise<MatterResponse> => {
    const response = await api.post(`/matters/${matterId}/move-client`, { client_id: clientId });
    invalidateAllMattersCache();
    return response.data;
  },

  /** Current user's matter usage vs their plan cap. Always fresh (no cache) so
   *  the gating UI is correct right after a create/delete. */
  getMatterUsage: async (): Promise<MatterUsage> => {
    const response = await api.get('/matters/usage');
    return response.data;
  },

  /**
   * Aggregate all matters across all of the user's clients (role-scoped via the
   * X-Active-Role header, newest-first). Backed by the single-query backend
   * endpoint ``GET /matters/all`` (2026-07-19) — replaces the earlier per-client
   * fan-out that issued one /matters request per client and, at 30+ clients,
   * risked saturating Cloud SQL db-f1-micro's ~25-conn ceiling. Each row carries
   * ``client_name`` so cross-client surfaces can label the owning client.
   *
   * The 60-second module-level TTL cache + in-flight dedup are preserved: they
   * still collapse the DocumentVaultPage-mount burst into one request. Call
   * ``invalidateAllMattersCache()`` after any mutation that adds/removes a matter.
   */
  listAllMatters: async (): Promise<MatterResponse[]> => {
    const now = Date.now();
    if (_allMattersCache && now - _allMattersCache.ts < ALL_MATTERS_TTL_MS) {
      return _allMattersCache.value;
    }
    if (_allMattersInFlight) {
      return _allMattersInFlight;
    }
    _allMattersInFlight = (async () => {
      try {
        const response = await api.get('/matters/all');
        const matters: MatterResponse[] = response.data?.matters ?? [];
        _allMattersCache = { ts: Date.now(), value: matters };
        return matters;
      } finally {
        _allMattersInFlight = null;
      }
    })();
    return _allMattersInFlight;
  },

  getTimeline: async (matterId: number, limit = 50): Promise<MatterTimelineResponse> => {
    const response = await api.get(`/matters/${matterId}/timeline`, { params: { limit } });
    return response.data;
  },

  /** B4b: cross-matter cause-list — every upcoming hearing + deadline across
   *  all active matters, soonest-first. */
  getCauseList: async (params?: {
    date_from?: string;
    date_to?: string;
    forum?: string;
    deadline_type?: string;
    include_completed?: boolean;
  }): Promise<CauseListResponse> => {
    const response = await api.get('/matters/cause-list', { params });
    return response.data;
  },

  /**
   * P1-4: cheap matter→client_id resolver. Used by legacy URLs like
   * `/diary/:caseId` (no query params) to discover their canonical
   * `/workspace/:clientId/matters/:matterId` URL. Throws on 404 (matter
   * not found OR not accessible to this user).
   */
  getAccessInfo: async (matterId: number): Promise<{ matter_id: number; client_id: string }> => {
    const response = await api.get(`/matters/${matterId}/access-info`);
    return response.data;
  },

  attachArtifact: async (matterId: number, body: MatterAttachRequest): Promise<MatterAttachResponse> => {
    const response = await api.post(`/matters/${matterId}/attach`, body);
    return response.data;
  },

  /**
   * Save-to-Matter for the deterministic limitation calculator. Writes the
   * computed expiry as a 'limitation' CaseDeadline with rule provenance.
   * Idempotent on (matter, type, date) — repeat calls return the existing
   * row with `already_exists: true`.
   */
  addLimitationDeadline: async (
    matterId: number,
    body: LimitationDeadlineCreate,
  ): Promise<MatterDeadlineResponse> => {
    const response = await api.post(`/matters/${matterId}/deadlines/limitation`, body);
    return response.data;
  },

  listMatters: async (clientId: string, filters?: { status?: string; case_type?: string }): Promise<MatterListResponse> => {
    const response = await api.get(`/clients/${clientId}/matters/`, { params: filters });
    return response.data;
  },

  getMatter: async (clientId: string, matterId: number): Promise<MatterResponse> => {
    const response = await api.get(`/clients/${clientId}/matters/${matterId}`);
    return response.data;
  },

  updateMatter: async (clientId: string, matterId: number, data: MatterUpdate): Promise<MatterResponse> => {
    const response = await api.patch(`/clients/${clientId}/matters/${matterId}`, data);
    return response.data;
  },

  deleteMatter: async (clientId: string, matterId: number): Promise<void> => {
    await api.delete(`/clients/${clientId}/matters/${matterId}`);
  },
};

export interface DefenceFlag {
  kind: 'overcharging' | 'bail' | 'quashing' | 'defence_lead' | 'verify' | 'special_act_bar' | 'point';
  text: string;
  section?: string | null;
  severity?: string | null;
}

export interface TheorySeed {
  facts: string;
  sections: string[];
  fir_number?: string | null;
  opposing_party?: string | null;
  defence_flags: DefenceFlag[];
  source: { kind: 'fir_analysis'; artifact_id: number; created_at: string };
}

/**
 * Seed the Case Theory intake from a matter's latest FIR analysis.
 * Returns null when the matter has no FIR analysis (backend replies 204).
 * Any error resolves to null — seeding must never block theory creation.
 */
export async function getTheorySeed(matterId: number): Promise<TheorySeed | null> {
  const r = await getTheorySeedStatus(matterId);
  return r.status === 'found' ? r.seed : null;
}

/**
 * Same read, but tells "this matter has NO FIR analysis" (204) apart from
 * "the lookup failed". Only the former may drive the FIR-first guidance —
 * a network blip must never tell the advocate their analysis is missing.
 */
export async function getTheorySeedStatus(
  matterId: number,
): Promise<{ status: 'found'; seed: TheorySeed } | { status: 'none' } | { status: 'error' }> {
  try {
    const res = await api.get<TheorySeed>(`/matters/${matterId}/theory-seed`);
    if (res.status === 204 || !res.data) return { status: 'none' };
    return { status: 'found', seed: res.data };
  } catch {
    return { status: 'error' };
  }
}
